import { useEffect, useRef } from 'react'

/**
 * Nitia isotype, animated on canvas.
 *
 * Ported from the design team's reference (topbar_nitia_motion.html). The mark is
 * a chain of metaballs: core, right node, three lobes hanging off an orbit and four
 * corner satellites. The intro builds that chain; the loop then breathes the settled
 * official path with a subtle scale pulse and satellite wobble.
 *
 * Geometry lives in the isotype's own 1742x1811 space and is scaled at draw time.
 */

type Point = { x: number; y: number }
type Range = [number, number]
type Easing = (t: number) => number

const ORBIT = 656.2
const C: Point = { x: 904.944, y: 905.944 }
const R_NODE = 248.742
const RIGHT: Point = { x: 1561.64, y: 905.944 }
const R_RIGHT = 180.55
const R_DOT = 93.867
const DOTS: Point[] = [
  { x: 248.867, y: 249.866 },
  { x: 1561.52, y: 249.866 },
  { x: 248.867, y: 1562.52 },
  { x: 1561.52, y: 1562.52 }
]
const NECK_V = { core: 0.715, lobe: 0.575 }

const CORE_D =
  'M1742.19 906.192C1742.19 1005.89 1661.34 1086.73 1561.64 1086.73C1535.85 1086.73 1511.55 1081.28 1489.48 1071.61C1483.77 1068.38 1478.07 1065.65 1472.12 1062.93C1432.44 1044.08 1389.28 1031.43 1343.9 1026.22C1328.53 1024.49 1312.9 1023.5 1297.03 1023.5H1296.04C1254.62 1023.5 1214.69 1029.94 1177 1041.35C1138.31 1053.25 1102.35 1070.61 1069.62 1092.69C1058.46 1100.13 1047.54 1108.31 1037.13 1116.99C998.936 1141.05 953.801 1154.69 905.441 1154.69C768.05 1154.69 656.699 1043.34 656.699 905.944C656.699 768.553 768.05 657.45 905.441 657.45C957.521 657.45 1005.63 673.57 1045.56 700.85C1051.02 705.314 1056.72 709.53 1062.42 713.498C1096.9 737.802 1135.58 757.145 1177 769.793C1214.45 781.449 1254.37 787.649 1295.54 787.897H1297.77C1313.4 787.897 1329.02 786.905 1344.15 785.169C1390.03 779.961 1433.68 767.313 1473.6 747.97C1478.56 745.738 1483.52 743.258 1488.24 740.53C1510.56 730.61 1535.36 724.906 1561.64 724.906C1661.34 724.906 1742.19 805.753 1742.19 905.448V906.192Z'
const BAND_D =
  'M1153.69 1562.39C1153.69 1699.79 1042.33 1810.89 904.944 1810.89C767.553 1810.89 656.45 1699.54 656.45 1562.39C656.45 1471.88 626.939 1388.05 576.843 1320.35C552.539 1287.61 523.523 1258.6 490.788 1234.29C423.084 1184.2 339.261 1154.69 248.742 1154.69C111.351 1154.69 0 1043.33 0 905.944C0 768.553 111.351 657.45 248.742 657.45C339.261 657.45 423.084 627.939 490.788 577.843C523.523 553.539 552.539 524.523 576.843 491.788C626.939 424.084 656.45 340.261 656.45 249.742C656.45 112.351 767.801 0.999878 904.944 0.999878C1042.09 0.999878 1153.69 112.351 1153.69 249.742C1153.69 387.133 1042.33 498.236 904.944 498.236C814.425 498.236 730.602 527.747 662.898 577.843C630.163 602.147 601.147 631.162 576.843 663.898C526.747 731.602 497.236 815.425 497.236 905.944C497.236 996.463 526.747 1080.29 576.843 1148.24C601.147 1180.97 630.163 1209.99 662.898 1234.29C730.602 1284.39 814.425 1313.9 904.944 1313.9C1042.33 1313.9 1153.69 1425.25 1153.69 1562.39Z'

const ISO_W = 1742.19
const ISO_H = 1810.89

// Built lazily once: the settled mark the loop segment draws.
let officialPath: Path2D | null = null
function official(): Path2D {
  if (!officialPath) {
    officialPath = new Path2D()
    officialPath.addPath(new Path2D(BAND_D))
    officialPath.addPath(new Path2D(CORE_D))
  }
  return officialPath
}

// Timings on the reference's 105-frame scale.
const T = {
  nucleus: [0, 16] as Range,
  detach: [13, 32] as Range,
  orbit: [26, 64] as Range,
  bud: [38, 68] as Range,
  dotsA: 46,
  dotsB: 52,
  dotDur: 14,
  settle: [60, 70] as Range
}
const FIN = 72
const BREAK_GAP = 34

// cycle: mount (2.0s) + breathe (1.4s) + dismount (the rest), per the design reference
const ENT = 2.0 // mount, seconds
const LOOP = 1.4 // breathe, seconds

function bezier(x1: number, y1: number, x2: number, y2: number): Easing {
  const cx = (t: number, a: number, b: number) =>
    ((1 - 3 * b + 3 * a) * t + (3 * b - 6 * a)) * t * t + 3 * a * t
  return (t: number) => {
    if (t <= 0) return 0
    if (t >= 1) return 1
    let lo = 0
    let hi = 1
    let m = t
    for (let i = 0; i < 22; i++) {
      m = (lo + hi) / 2
      if (cx(m, x1, x2) < t) lo = m
      else hi = m
    }
    return cx(m, y1, y2)
  }
}

const easeOut = bezier(0.22, 1, 0.36, 1)
const easeOutSoft = bezier(0.33, 1, 0.68, 1)
const easePull = bezier(0.85, 0, 0.4, 1)
const easeCreep = bezier(0.5, 0, 0.2, 1)
const easePop = bezier(0.16, 1.07, 0.3, 1)
const easeBorn = bezier(0.16, 1.02, 0.3, 1)

function at(f: number, r: Range, v: Range, e?: Easing): number {
  let t = (f - r[0]) / (r[1] - r[0])
  t = t < 0 ? 0 : t > 1 ? 1 : t
  return v[0] + (v[1] - v[0]) * (e || easeOut)(t)
}

/** The neck between two drops: computed, not a "goo" filter. */
function neck(c1: Point, r1: number, c2: Point, r2: number, v: number): string | null {
  const dx = c2.x - c1.x
  const dy = c2.y - c1.y
  const d = Math.hypot(dx, dy)
  if (r1 <= 0 || r2 <= 0 || d <= Math.abs(r1 - r2)) return null

  let u1 = 0
  let u2 = 0
  if (d < r1 + r2) {
    u1 = Math.acos((r1 * r1 + d * d - r2 * r2) / (2 * r1 * d))
    u2 = Math.acos((r2 * r2 + d * d - r1 * r1) / (2 * r2 * d))
  }
  const base = Math.atan2(dy, dx)
  const spread = Math.acos((r1 - r2) / d)
  const a1 = base + u1 + (spread - u1) * v
  const a2 = base - u1 - (spread - u1) * v
  const a3 = base + Math.PI - u2 - (Math.PI - u2 - spread) * v
  const a4 = base - Math.PI + u2 + (Math.PI - u2 - spread) * v

  const al = (c: Point, a: number, r: number): Point => ({
    x: c.x + Math.cos(a) * r,
    y: c.y + Math.sin(a) * r
  })
  const p1 = al(c1, a1, r1)
  const p2 = al(c1, a2, r1)
  const p3 = al(c2, a3, r2)
  const p4 = al(c2, a4, r2)

  const tot = r1 + r2
  const grip =
    Math.min(v * 2.4, Math.hypot(p3.x - p1.x, p3.y - p1.y) / tot) * Math.min(1, (d * 2) / tot)
  const h1 = al(p1, a1 - Math.PI / 2, r1 * grip)
  const h2 = al(p2, a2 + Math.PI / 2, r1 * grip)
  const h3 = al(p3, a3 + Math.PI / 2, r2 * grip)
  const h4 = al(p4, a4 - Math.PI / 2, r2 * grip)

  const far = d > r1 ? 1 : 0
  const f = (n: number) => n.toFixed(1)
  return (
    'M' + f(p1.x) + ' ' + f(p1.y) +
    'C' + f(h1.x) + ' ' + f(h1.y) + ' ' + f(h3.x) + ' ' + f(h3.y) + ' ' + f(p3.x) + ' ' + f(p3.y) +
    'A' + f(r2) + ' ' + f(r2) + ' 0 ' + far + ' 0 ' + f(p4.x) + ' ' + f(p4.y) +
    'C' + f(h4.x) + ' ' + f(h4.y) + ' ' + f(h2.x) + ' ' + f(h2.y) + ' ' + f(p2.x) + ' ' + f(p2.y) +
    'A' + f(r1) + ' ' + f(r1) + ' 0 ' + far + ' 0 ' + f(p1.x) + ' ' + f(p1.y) + 'Z'
  )
}

function detachAt(f: number) {
  const p = at(f, T.detach, [0, 1], easePull)
  const d = R_NODE + (ORBIT - R_NODE) * p
  const r = R_NODE * at(f, [T.detach[0], T.detach[0] + 8], [0, 1], easeOutSoft)
  return { p, d, r, gap: d - R_NODE - r }
}

const SNAP = (() => {
  for (let f = T.detach[0]; f <= T.detach[1]; f++) {
    if (detachAt(f).gap > BREAK_GAP) return f
  }
  return T.detach[1]
})()

const SEED_DEG = ((R_NODE / ORBIT) * 180) / Math.PI
const RIGHT_D = RIGHT.x - C.x
const onOrbit = (deg: number): Point => ({
  x: C.x + ORBIT * Math.cos((deg * Math.PI) / 180),
  y: C.y + ORBIT * Math.sin((deg * Math.PI) / 180)
})
const LEFT0 = onOrbit(180)

/** Draws the isotype in the large space. f in [0, FIN]; dotK: 4 satellite multipliers. */
function drawIso(ctx: CanvasRenderingContext2D, f: number, dotK: number[] | null) {
  const settled = f >= T.settle[1]

  if (!settled) {
    const born = at(f, T.nucleus, [0, 1], easeBorn)
    const squash = 0.09 * Math.sin(Math.PI * born)
    const det = detachAt(f)
    const attached = det.gap <= BREAK_GAP && f >= T.detach[0]
    const tension = attached
      ? Math.pow(det.p, 1.4)
      : at(f, [SNAP, SNAP + 5], [1, 0], easeOutSoft)

    const nucR = R_NODE * born
    const nuc = {
      cx: C.x - 24 * tension,
      cy: C.y,
      rx: nucR * (1 + squash + 0.07 * tension),
      ry: nucR * (1 - squash - 0.05 * tension)
    }
    const nucC: Point = { x: nuc.cx, y: nuc.cy }
    const leftR = det.r
    const leftC: Point = { x: C.x - det.d, y: C.y }

    const roll = at(f, T.orbit, [0, 1], easeCreep)
    const lobeR = R_NODE * at(f, [T.orbit[0], T.orbit[0] + 11], [0, 1], easeOutSoft)
    const sweep = SEED_DEG + (90 - SEED_DEG) * roll
    const off = (deg: number): Point => {
      const o = onOrbit(deg)
      return { x: leftC.x + (o.x - LEFT0.x), y: leftC.y + (o.y - LEFT0.y) }
    }
    const topC = off(180 + sweep)
    const botC = off(180 - sweep)
    const started = f >= T.orbit[0]

    const bud = at(f, T.bud, [0, 1], easeCreep)
    const budR = at(f, [T.bud[0], T.bud[0] + 9], [0, R_RIGHT], easeOutSoft)
    const rightC: Point = { x: C.x + R_NODE + (RIGHT_D - R_NODE) * bud, y: RIGHT.y }

    const necks: (string | null)[] = [
      nucR > 1 && budR > 1 ? neck(nucC, nucR, rightC, budR, NECK_V.core) : null,
      started && lobeR > 1 ? neck(leftC, leftR, topC, lobeR, NECK_V.lobe) : null,
      started && lobeR > 1 ? neck(leftC, leftR, botC, lobeR, NECK_V.lobe) : null,
      attached && nucR > 1 && leftR > 1
        ? neck(
            nucC,
            nucR,
            leftC,
            leftR,
            Math.max(
              0.07,
              Math.min(0.62, 0.62 + ((det.gap + 320) * (0.07 - 0.62)) / (BREAK_GAP + 320))
            )
          )
        : null
    ]
    for (const d of necks) {
      if (d) ctx.fill(new Path2D(d), 'nonzero')
    }

    const disc = (c: Point, r: number) => {
      if (r > 0.5) {
        ctx.beginPath()
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2)
        ctx.fill()
      }
    }
    disc(topC, started ? lobeR : 0)
    disc(botC, started ? lobeR : 0)
    disc(leftC, f >= T.detach[0] ? leftR : 0)
    disc(rightC, budR)

    if (nucR > 0.5) {
      ctx.beginPath()
      ctx.ellipse(nuc.cx, nuc.cy, nuc.rx, nuc.ry, 0, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  const oa = settled ? 1 : at(f, T.settle, [0, 1], easeOutSoft)
  if (oa > 0.002) {
    const ga = ctx.globalAlpha
    ctx.globalAlpha = ga * oa
    ctx.fill(official(), 'nonzero')
    ctx.globalAlpha = ga
  }

  const arrival = [T.dotsA, T.dotsB, T.dotsB, T.dotsA]
  for (let j = 0; j < 4; j++) {
    const k = at(f, [arrival[j], arrival[j] + T.dotDur], [0, 1], easePop)
    const p = DOTS[j]
    const dd = 30 * (1 - k)
    const r = R_DOT * k * (dotK ? dotK[j] : 1)
    if (r > 0.2) {
      ctx.beginPath()
      ctx.arc(p.x + (p.x < C.x ? dd : -dd), p.y + (p.y < C.y ? dd : -dd), r, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

type Layout = { W: number; s: number; ox: number; oy: number }

/** Square framing for the isotype at a given device-pixel size. */
function layout(px: number): Layout {
  const pad = 0.1 * ISO_H
  const side = Math.max(ISO_W, ISO_H) + 2 * pad
  const W = Math.round(px)
  return { W, s: W / side, ox: (ISO_W - side) / 2, oy: (ISO_H - side) / 2 }
}

type Segment = 'in' | 'loop' | 'out'

/** Renders one full frame. `color` is any CSS colour for the mark. */
function renderFrame(
  ctx: CanvasRenderingContext2D,
  L: Layout,
  seg: Segment,
  u: number,
  color: string
) {
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, L.W, L.W)
  ctx.setTransform(L.s, 0, 0, L.s, -L.ox * L.s, -L.oy * L.s)
  ctx.fillStyle = color

  let f: number
  let scale = 1
  let dotK: number[] | null = null

  if (seg === 'in') {
    f = FIN * u
  } else if (seg === 'out') {
    // dismount: the intro played backwards
    f = FIN * (1 - u)
  } else {
    f = FIN
    // breathing: a 1.4% pulse, plus a travelling wobble across the satellites
    scale = 1 + 0.014 * Math.sin(2 * Math.PI * u)
    const win = Math.pow(Math.sin(Math.PI * u), 2)
    dotK = []
    for (let j = 0; j < 4; j++) {
      dotK.push(1 + 0.11 * win * Math.sin(2 * Math.PI * (2 * u - j / 4)))
    }
  }

  ctx.save()
  ctx.translate(C.x, C.y)
  ctx.scale(scale, scale)
  ctx.translate(-C.x, -C.y)
  drawIso(ctx, f, dotK)
  ctx.restore()

  ctx.setTransform(1, 0, 0, 1, 0, 0)
}

/**
 * One unbroken cycle: mount, breathe, dismount — then straight into the next.
 * Mount and breathe keep their absolute durations; the dismount takes whatever
 * the period leaves, so a longer period means a longer, softer dismount.
 */
function sampleTimeline(t: number, period: number): { seg: Segment; u: number } {
  const cycle = t % period
  if (cycle < ENT) return { seg: 'in', u: cycle / ENT }
  if (cycle < ENT + LOOP) return { seg: 'loop', u: (cycle - ENT) / LOOP }
  const outDur = Math.max(0.1, period - ENT - LOOP)
  return { seg: 'out', u: Math.min(1, (cycle - ENT - LOOP) / outDur) }
}

type NitiaLogoProps = {
  /** Rendered size in CSS pixels. */
  size?: number
  /** Any CSS colour for the mark. */
  color?: string
  /** Full cycle length in seconds. Must exceed mount + breathe (3.4s). */
  period?: number
  className?: string
}

export function NitiaLogo({
  size = 12,
  color = '#FFFFFF',
  period = 4,
  className
}: NitiaLogoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const px = size * dpr
    canvas.width = px
    canvas.height = px
    const L = layout(px)

    // Honour reduced-motion: draw the settled mark once, never animate.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (reduced?.matches) {
      renderFrame(ctx, L, 'loop', 0, color)
      return
    }

    let raf = 0
    let start = performance.now()
    let elapsed = 0 // frozen animation clock, so hiding the tab pauses rather than skips

    const frame = (now: number) => {
      elapsed = (now - start) / 1000
      const { seg, u } = sampleTimeline(elapsed, period)
      renderFrame(ctx, L, seg, u, color)
      raf = requestAnimationFrame(frame)
    }

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf)
        raf = 0
      } else if (!raf) {
        start = performance.now() - elapsed * 1000
        raf = requestAnimationFrame(frame)
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [size, color, period])

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: size, height: size, display: 'block' }}
      aria-hidden="true"
    />
  )
}
